import React from 'react'
import { DateFormats, formatISOString } from 'utils'
import { Cell, ExpandCell, Name, DataRow, DynamicRow } from 'uiComponents/table'
import { WeekdayPattern } from 'uiComponents/weekdayPattern'
import { ActionButtonA } from 'uiComponents/buttons'
import { InlineEdit } from 'uiComponents/input'
import { TimeSlotGroup, TimeSlotGroupStatus } from './timeSlotsService'
import { DateRange } from 'uiComponents/date'
import classNames from 'classnames'
import { StatusText, StatusColumn, StatusStyle } from 'uiComponents/statusText'
import { StatusChip } from 'uiComponents/statusChip/statusChip'
import { TIME_SLOT_STATUS_TONE } from './statusTone'
import { useTranslation } from 'translations'

interface TimeSlotsListPageProps {
    accountSlug: string
    timeslot: TimeSlotGroup
    productsView: boolean
    productsViewFeatureOn: boolean
    isExpanded: boolean
    toggleExpanded: () => void
    onPriorityUpdateConfirm: (priority: string, id: string) => void
    hasPermission: (permission: string, accountSlug: string) => boolean
    redesign?: boolean
    describeTarget?: boolean
}

export function TimeSlotsListRow(props: TimeSlotsListPageProps) {
    const { t } = useTranslation()
    const numberOfCategories = props.timeslot.productsLists.length
    const expandable = !props.productsView && !!numberOfCategories
    const categoryName =
        props.productsViewFeatureOn && !!numberOfCategories
            ? t('timeSlots.listRow.categoryCount', { count: numberOfCategories })
            : props.timeslot.productsListNames
    // A product group names its product, a category group its categories: the view only decides when no
    // selection says otherwise.
    const showProduct = props.describeTarget ? !!props.timeslot.article : props.productsView
    const categoryCellContent = showProduct ? props.timeslot.article?.name || '' : categoryName
    const queryString = props.productsViewFeatureOn
        ? `/?timeslots_view=${props.productsView ? 'products' : 'categories'}`
        : ''

    const TimeSlotRow = props.productsView ? DataRow : DynamicRow

    const TimeSlotGroupStatusStyleMap: Record<TimeSlotGroupStatus, StatusStyle> = {
        active: 'success',
        upcoming: 'warn',
        expired: 'error',
    }

    const TimeSlotGroupStatusLabelMap: Record<TimeSlotGroupStatus, string> = {
        active: t('timeSlots.listRow.status.active'),
        upcoming: t('timeSlots.listRow.status.upcoming'),
        expired: t('timeSlots.listRow.status.expired'),
    }

    function getTimeRangeValue(timeslot: TimeSlotGroup) {
        if (!!timeslot.availableFrom && !!timeslot.availableTo) {
            return <DateRange startDateString={timeslot.availableFrom} endDateString={timeslot.availableTo} />
        }
        if (!!timeslot.availableFrom) {
            return t('timeSlots.listRow.dateFrom', {
                date: formatISOString(timeslot.availableFrom, DateFormats.LONG_DATE),
            })
        }
        if (!!timeslot.availableTo) {
            return t('timeSlots.listRow.dateUntil', {
                date: formatISOString(timeslot.availableTo, DateFormats.LONG_DATE),
            })
        }
        return ''
    }

    return (
        <TimeSlotRow
            interactive={expandable}
            className={classNames('timeSlotRow', {
                inactive: props.timeslot.status !== 'active',
                expanded: props.isExpanded,
                'timeslots-list-row': props.redesign,
                expired: props.redesign && props.timeslot.status === 'expired',
            })}
            onClick={expandable ? props.toggleExpanded : () => {}}
        >
            {props.productsViewFeatureOn && (
                <>
                    {!!props.timeslot.productsLists.length ? (
                        <ExpandCell className="expand" expanded={props.isExpanded} align="right" noPadding />
                    ) : (
                        <Cell noPadding />
                    )}
                </>
            )}
            <Cell title={props.timeslot.name}>
                <StatusColumn>
                    <Name className="name">{props.timeslot.name}</Name>
                    {props.redesign ? (
                        <StatusChip
                            label={t(`capacity.timeSlots.status.${props.timeslot.status}`)}
                            tone={TIME_SLOT_STATUS_TONE[props.timeslot.status]}
                        />
                    ) : (
                        <StatusText statusStyle={TimeSlotGroupStatusStyleMap[props.timeslot.status]}>
                            {TimeSlotGroupStatusLabelMap[props.timeslot.status]}
                        </StatusText>
                    )}
                </StatusColumn>
            </Cell>
            <Cell className="category withName">
                <span title={categoryCellContent}>{categoryCellContent}</span>
            </Cell>
            <Cell className="priority">
                <InlineEdit
                    isNumber
                    integerOnly
                    min={1}
                    max={1000}
                    id="input"
                    limitedTextWidth="5em"
                    value={props.timeslot.priority ? props.timeslot.priority : ''}
                    placeholder={t('timeSlots.listRow.priorityPlaceholder')}
                    onEditAccept={(v) => props.onPriorityUpdateConfirm(v, props.timeslot.id)}
                    disabled={!props.hasPermission('partner_admin', props.accountSlug)}
                />
            </Cell>
            <Cell>
                {props.timeslot.weekdays && (
                    <WeekdayPattern
                        weekdays={
                            props.timeslot.weekdays.length > 0 ? props.timeslot.weekdays.toString() : '0,1,2,3,4,5,6'
                        }
                        type="strict"
                    />
                )}
            </Cell>
            <Cell className="dates wrapContent">{getTimeRangeValue(props.timeslot)}</Cell>
            <Cell>
                <ActionButtonA
                    className="editTimeSlot"
                    kind="action"
                    secondary
                    href={`/account/${props.accountSlug}/products/time_slots/edit/${props.timeslot.id}${queryString}`}
                >
                    {t('common.edit')}
                </ActionButtonA>
            </Cell>
        </TimeSlotRow>
    )
}
